import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { User, Mail, Instagram, Briefcase, Users, FileText } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';

const Field = ({ icon: Icon, label, children }) => (
  <div className="input-group">
    <label className="input-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <Icon size={13} style={{ color: 'var(--primary)' }} />
      {label}
    </label>
    {children}
  </div>
);

const CoachRequestPage = () => {
  const { t } = useTranslation();
  const [showSuccess, setShowSuccess] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    instagram: '',
    experience: '',
    clients: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: false }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = true;
    if (!form.email.trim() || !form.email.includes('@')) newErrors.email = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await apiFetch('/api/coach/request', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Error enviando solicitud');
      setShowSuccess(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" style={{ justifyContent: 'flex-start', paddingTop: '32px' }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <button
          className="btn btn-sm btn-secondary mb-4"
          onClick={() => navigate(-1)}
          style={{ alignSelf: 'flex-start' }}
        >
          {t('common.back')}
        </button>

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '72px', height: '72px', borderRadius: '20px',
            background: 'rgba(var(--primary-rgb),0.1)', border: '1px solid rgba(var(--primary-rgb),0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: '32px'
          }}>
            🐉
          </div>
          <h1 style={{ fontSize: '28px', color: 'var(--foreground)', marginBottom: '8px' }}>
            {t('coach_request.title')}
          </h1>
          <p style={{ fontSize: '14px', color: 'var(--muted-foreground)', lineHeight: '1.5' }}>
            {t('coach_request.desc')}
          </p>
        </div>

        <div className="card" style={{ gap: '0' }}>
          <Field icon={User} label={t('coach_request.name_label')}>
            <input
              className="input"
              placeholder={t('coach_request.name_placeholder')}
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              style={errors.name ? { borderColor: '#ef4444' } : {}}
            />
            {errors.name && <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>{t('coach_request.field_required')}</p>}
          </Field>

          <Field icon={Mail} label={t('coach_request.email_label')}>
            <input
              className="input"
              type="email"
              placeholder={t('coach_request.email_placeholder')}
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
              style={errors.email ? { borderColor: '#ef4444' } : {}}
            />
            {errors.email && <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>{t('coach_request.email_invalid')}</p>}
          </Field>

          <Field icon={Instagram} label={t('coach_request.instagram_label')}>
            <input
              className="input"
              placeholder={t('coach_request.instagram_placeholder')}
              value={form.instagram}
              onChange={(e) => handleChange('instagram', e.target.value)}
            />
          </Field>

          <Field icon={Briefcase} label={t('coach_request.experience_label')}>
            <input
              className="input"
              placeholder={t('coach_request.experience_placeholder')}
              value={form.experience}
              onChange={(e) => handleChange('experience', e.target.value)}
            />
          </Field>

          <Field icon={Users} label={t('coach_request.clients_label')}>
            <input
              className="input"
              placeholder={t('coach_request.clients_placeholder')}
              value={form.clients}
              onChange={(e) => handleChange('clients', e.target.value)}
            />
          </Field>

          <Field icon={FileText} label={t('coach_request.about_label')}>
            <textarea
              className="input"
              rows={4}
              placeholder={t('coach_request.about_placeholder')}
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              style={{ resize: 'vertical', lineHeight: '1.5' }}
            />
          </Field>

          <button
            className="btn btn-primary w-full"
            onClick={handleSubmit}
            disabled={loading}
            style={{ marginTop: '8px' }}
          >
            {loading
              ? <div className="loading-spinner" style={{ width: '20px', height: '20px' }} />
              : t('coach_request.submit_btn')
            }
          </button>
        </div>

        <p style={{ textAlign: 'center', fontSize: '12px', color: 'var(--muted-foreground)', marginTop: '16px' }}>
          {t('coach_request.footer_note')}
        </p>
      </div>

      {showSuccess && createPortal(
        <div className="modal-overlay">
          <div className="modal" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
            <h2 style={{ marginBottom: '12px', color: 'var(--primary)' }}>{t('coach_request.success_title')}</h2>
            <p style={{ fontSize: '14px', color: 'var(--muted-foreground)', marginBottom: '24px', lineHeight: '1.6' }}>
              {t('coach_request.success_desc')}
            </p>
            <button className="btn btn-primary w-full" onClick={() => { setShowSuccess(false); navigate('/login'); }}>
              {t('coach_request.back_to_login')}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CoachRequestPage;
